import {useFormik} from 'formik';
import * as Yup from 'yup';
import {useState} from 'react';
import {AxiosError} from 'axios';
import {useFundraiser} from '../../../entities';
import {BenefactorTransferFormData, BenefactorTransferDTO} from '../../../entities';
import {normalizeTransferAmount, parseTransferAmount} from '../lib/transferAmount';

interface UseBenefactorTransferFormProps {
  fundraiserId: string;
  setOpen: (open: boolean) => void;
  onSuccess?: () => void;
}

export const useBenefactorTransferForm = ({fundraiserId, setOpen, onSuccess}: UseBenefactorTransferFormProps) => {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formDataForConfirm, setFormDataForConfirm] = useState<BenefactorTransferDTO | null>(null);
  const {transferFromBenefactorMutation} = useFundraiser({id: fundraiserId});

  const validationSchema = Yup.object({
    benefactorId: Yup.string().required('Выберите донора'),
    amount: Yup.string()
      .required('Сумма обязательна')
      .test('amount-format', 'Должно быть числом', (value, context) => {
        if (!value || parseTransferAmount(value) !== null) return true;
        return normalizeTransferAmount(value) === value
          ? context.createError({message: 'Сумма слишком велика для точной передачи копеек'})
          : false;
      })
      .test('amount-positive', 'Сумма должна быть положительной', (value) => {
        const amount = value ? parseTransferAmount(value) : null;
        return amount === null || amount > 0;
      }),

    serviceFee: Yup.string()
      .required('Комиссия обязательна')
      .test('fee-format', 'Должно быть числом', (value, context) => {
        if (!value || parseTransferAmount(value) !== null) return true;
        return normalizeTransferAmount(value) === value
          ? context.createError({message: 'Сумма слишком велика для точной передачи копеек'})
          : false;
      })
      .test('fee-min', 'Комиссия не может быть отрицательной', (value) => {
        const serviceFee = value ? parseTransferAmount(value) : null;
        return serviceFee === null || serviceFee >= 0;
      }),
    bookkeepingNumber: Yup.string(),
    note: Yup.string(),
  });

  const onSubmit = async (values: BenefactorTransferFormData) => {
    const amount = parseTransferAmount(values.amount);
    const serviceFee = parseTransferAmount(values.serviceFee);
    if (amount === null || amount <= 0 || serviceFee === null || serviceFee < 0) return;

    const transferData: BenefactorTransferDTO = {
      benefactorId: values.benefactorId,
      amount,
      serviceFee,
      bookkeepingNumber: values.bookkeepingNumber || null,
      note: values.note || null,
    };

    setFormDataForConfirm(transferData);
    setShowConfirmModal(true);
  };

  const handleConfirmTransfer = async () => {
    if (!formDataForConfirm) return;

    try {
      await transferFromBenefactorMutation.mutateAsync(formDataForConfirm);
      setShowConfirmModal(false);
      setOpen(false);
      formik.resetForm();

      onSuccess?.();
      setTimeout(() => {
        window.location.reload();
      }, 2500);
    } catch (error) {
      console.error('Ошибка при внесении средств:', error);
    }
  };

  const formik = useFormik<BenefactorTransferFormData>({
    initialValues: {
      benefactorId: '',
      amount: '',
      serviceFee: '',
      bookkeepingNumber: '',
      note: '',
    },
    validationSchema,
    onSubmit,
  });

  return {
    ...formik,
    setFieldTouched: formik.setFieldTouched,
    isLoading: transferFromBenefactorMutation.isPending,
    submitError: transferFromBenefactorMutation.error
      ? (transferFromBenefactorMutation.error as AxiosError<ApiError>).response?.data
      : undefined,
    showConfirmModal,
    setShowConfirmModal,
    formDataForConfirm,
    handleConfirmTransfer,
    submitForm: () => formik.handleSubmit(),
  };
};
